import React, { useEffect, useMemo, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, FieldSelect, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import { callToSearchCpf } from '../../../util/api';
import { debounce } from 'lodash';
import { validateCpf } from '../../../util/string';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => {
  const [currentStep, setCurrentStep] = useState(1);

  // Inform parent component about current step
  React.useEffect(() => {
    if (props.onStepChange) {
      props.onStepChange(currentStep);
    }
  }, [currentStep, props.onStepChange]);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
        } = formRenderProps;

        const { userType } = values || {};

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordFormat = validators.validPassword(intl.formatMessage({ id: 'SignupForm.passwordFormat' }));
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength,
          passwordFormat,
        );

        //Arrays for selects options
        const howDidYouFindUsOptions = [
          { value: 'google', labelId: 'SignupForm.howDidYouFindUs.google' },
          { value: 'social', labelId: 'SignupForm.howDidYouFindUs.social' },
          { value: 'referral', labelId: 'SignupForm.howDidYouFindUs.referral' },
          { value: 'other', labelId: 'SignupForm.howDidYouFindUs.other' }
        ];

        const representativeOptions = [
          { value: 'rep1', labelId: 'SignupForm.representative.rep1' },
          { value: 'rep2', labelId: 'SignupForm.representative.rep2' }
        ];

        // Custom user fields. Since user types are not supported here,
        // only fields with no user type id limitation are selected.
        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;
        const handleContinueStep1 = (e) => {
          e.preventDefault();
          // Validate email and password
          const { email, password, userType } = values || {};
          if (email && password && userType) {
            setCurrentStep(2);
          }
        };

        const [searchingCpf, setSearchingCpf] = useState(false);
        const [searchedCpf, setSearchedCpf] = useState(false);
        const [validCpf, setValidCpf] = useState(false);

        /* const useDebouncedCpf = (cpf, delay = 500) => {
          const validateCpf = useMemo(
            () =>
              debounce(cpf => {
                if (!cpf || cpf.trim() === '') {
                  setValidCpf(false);
                  setSearchedCpf(false);
                  return;
                }

                setSearchingCpf(true);
                setSearchedCpf(false);
                setValidCpf(false);

                callToSearchCpf({ cpf })
                  .then(res => {
                    console.log(res);
                    setValidCpf(false);
                  })
                  .catch(err => {
                    setValidCpf(false);
                  })
                  .finally(() => {
                    setSearchedCpf(true);
                    setSearchingCpf(false);
                  });
              }, delay),
            [delay]
          );

          useEffect(() => {
            if (cpf) {
              validateCpf(cpf);
            }

            return () => {
              validateCpf.cancel();
            };
          }, [cpf, validateCpf]);
        };

        useDebouncedCpf(values.cpf || ''); */

        return (
          <Form className={classes} onSubmit={currentStep === 1 ? handleContinueStep1 : handleSubmit}>
            {currentStep === 1 && (
              <div className={css.step1}>
                <div className={css.defaultUserFields}>
                  <FieldTextInput
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={intl.formatMessage({
                      id: 'SignupForm.emailLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.emailPlaceholder',
                    })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />

                  <FieldTextInput
                    className={css.password}
                    type="password"
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({
                      id: 'SignupForm.passwordLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.passwordPlaceholder',
                    })}
                    validate={passwordValidators}
                  />

                  <FieldSelectUserType
                    name="userType"
                    userTypes={userTypes}
                    hasExistingUserType={!!preselectedUserType}
                    intl={intl}
                  />
                </div>

                <div className={css.bottomWrapper}>
                  <PrimaryButton
                    type="submit" inProgress={submitInProgress}
                    disabled={!values?.email || !values?.password || !values?.userType}
                  >
                    <FormattedMessage id="SignupForm.continueButton" />
                  </PrimaryButton>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className={css.step2}>
                <h2><FormattedMessage id="SignupForm.welcomeTitle" /></h2>
                <p><FormattedMessage id="SignupForm.completeRegistration" /></p>

                {userType === 'customer' && (
                  <div className={css.customerFields}>
                    <FieldTextInput
                      className={css.row}
                      type="text"
                      id={formId ? `${formId}.displayName` : 'displayName'}
                      name="displayName"
                      label={intl.formatMessage({
                        id: 'SignupForm.firstNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.firstNamePlaceholder',
                      })}
                      validate={validators.composeValidators(
                        validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.firstNameRequired',
                          })
                        ), validators.multiWordString(
                          intl.formatMessage({
                            id: 'SignupForm.firstNameRequired',
                          })
                        )
                      )}
                    />
                    {/* <FieldTextInput
                      className={css.firstNameRoot}
                      type="text"
                      id={formId ? `${formId}.fname` : 'fname'}
                      name="fname"
                      autoComplete="given-name"
                      label={intl.formatMessage({
                        id: 'SignupForm.firstNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.firstNamePlaceholder',
                      })}
                      validate={validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.firstNameRequired',
                        })
                      )}
                    />
                    <FieldTextInput
                      className={css.lastNameRoot}
                      type="text"
                      id={formId ? `${formId}.lname` : 'lname'}
                      name="lname"
                      autoComplete="family-name"
                      label={intl.formatMessage({
                        id: 'SignupForm.lastNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.lastNamePlaceholder',
                      })}
                      validate={validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.lastNameRequired',
                        })
                      )}
                    /> */}

                    <UserFieldPhoneNumber
                      formName="SignupForm"
                      className={css.row}
                      userTypeConfig={userTypeConfig}
                      intl={intl}
                    />

                    {showCustomUserFields ? (
                      <div className={css.customFields}>
                        {userFieldProps
                          .filter(p => ['pub_howDidYouFindUs'].includes(p.key))
                          .map(({ key, ...fieldProps }) => (
                            <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                          ))}
                      </div>
                    ) : null}

                    {/* termsAndConditions */}
                    <div className={css.bottomWrapper}>
                      {termsAndConditions}
                      <PrimaryButton
                        type="submit"
                        inProgress={submitInProgress}
                        disabled={submitDisabled}
                      >
                        <FormattedMessage id="SignupForm.registerButton" />
                      </PrimaryButton>
                    </div>

                  </div>
                )}

                {userType === 'provider' && (
                  <div className={css.businessFields}>
                    <FieldTextInput
                      className={css.row}
                      type="text"
                      id={formId ? `${formId}.cpf` : 'cpf'}
                      name="cpf"
                      label={intl.formatMessage({
                        id: 'SignupForm.cpfLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.cpfPlaceholder',
                      })}
                      validate={(cpf) => validateCpf(cpf, intl)}
                    />

                    <FieldTextInput
                      className={css.row}
                      type="text"
                      id={formId ? `${formId}.userDisplayName` : 'userDisplayName'}
                      name="userDisplayName"
                      label={intl.formatMessage({
                        id: 'SignupForm.displayNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.displayNamePlaceholder',
                      })}
                      validate={validators.composeValidators(
                        validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.displayNameRequired',
                          })
                        ), validators.multiWordString(
                          intl.formatMessage({
                            id: 'SignupForm.displayNameRequired',
                          })
                        )
                      )}
                    />

                    <FieldTextInput
                      className={css.row}
                      type="text"
                      id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                      name="phoneNumber"
                      label={intl.formatMessage({
                        id: 'SignupForm.phoneNumberLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.phoneNumberPlaceholder',
                      })}
                      validate={validators.composeValidators(
                        validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.phoneNumberRequired',
                          })
                        ),
                        validators.validPhone(
                          intl.formatMessage({ id: 'SignupForm.phoneNumberInvalid' })
                        ),
                      )}
                    />

                    {showCustomUserFields ? (
                      <div className={css.customFields}>
                        {userFieldProps
                          .filter(p => ['pub_howDidYouFindUs', 'pub_representativeName'].includes(p.key))
                          .map(({ key, ...fieldProps }) => (
                            <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                          ))}
                      </div>
                    ) : null}

                    <div className={css.bottomWrapper}>
                      {termsAndConditions}
                      <PrimaryButton
                        type="submit"
                        inProgress={submitInProgress}
                        disabled={submitDisabled}
                      >
                        <FormattedMessage id="SignupForm.registerButton" />
                      </PrimaryButton>
                    </div>

                  </div>
                )}
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
